import { apiGetOrNull, apiRequest } from '@/domains/api';
import type { IPage } from './oversight-contracts';
import type {
  IAccountQuery,
  IAdminAccountDetail,
  IAdminAccountRow,
  IAdminIdpInvitation,
  ICreateAccount,
  IIdpInvitationQueued,
  IInviteToIdp,
  ISetAccountEmail,
  ISetAccountRoles,
  ISetAccountStatus
} from './account-contracts';

/**
 * survey-api's account routes — the list (SR-124), the address lookup (SR-210), the create,
 * SR-228's roles write, SR-416's address, SR-442's status and SR-476's IdP invitations and
 * unlink.
 *
 * Its own module rather than a section of `oversight-api.ts`, following the split that file
 * already describes: this reads and writes **identity**, not responses, and it answers to its
 * own policies — `accounts.browse`, `accounts.manage` and `roles.manage` — none of which the
 * oversight surface's `responses.*` implies.
 *
 * The paths do not share a prefix, which is not a mistake here: everything but the create is
 * under `/api/v1/admin`, and the create is mapped at the **root** beside `/auth`. See
 * `account-contracts.ts`.
 */

const ADMIN_BASE = '/api/v1/admin';

/** Said to whoever opened the user card, or an account on the hub for a viewer holding
 *  `accounts.browse` (R13 reads it by address too), because the alternative is a request about
 *  a different route dressed up as an answer about this account. It is a limit of addressing
 *  the account by address, not a statement about the account: survey-api's id lookup can still
 *  reach it, and nothing about the row is wrong. */
const UNADDRESSABLE =
  'This address cannot be looked up by address: a "." or ".." between its slashes is removed ' +
  'from the URL before the request is sent, which would ask about a different ' +
  'route entirely. The account itself is fine — it can be read by account id.';

export const accountsApi = {
  /**
   * Who can log in — filtered, paged, ordered `createdAt DESC`.
   *
   * `kind`, `roleId` and `status` repeat once per value the way `listResponses` repeats
   * `status`; `email` and `name` are substrings. An unrecognised value is a 400 carrying a
   * problem per bad value, not an empty page.
   */
  listAccounts(
    query: IAccountQuery = {},
    signal?: AbortSignal
  ): Promise<IPage<IAdminAccountRow>> {
    const params = new URLSearchParams();
    const scalars: [string, string | number | undefined][] = [
      ['email', query.email],
      ['name', query.name],
      ['page', query.page],
      ['pageSize', query.pageSize]
    ];
    for (const [key, value] of scalars) {
      if (value === undefined || value === '') continue;
      params.append(key, String(value));
    }
    for (const kind of query.kind ?? []) params.append('kind', kind);
    for (const roleId of query.roleId ?? []) params.append('roleId', roleId);
    for (const status of query.status ?? []) params.append('status', status);

    const rendered = params.toString();
    return apiRequest<IPage<IAdminAccountRow>>(
      `${ADMIN_BASE}/accounts${rendered ? `?${rendered}` : ''}`,
      { signal }
    );
  },

  /**
   * One account, addressed — **exactly**, and `null` when nobody is registered there.
   *
   * The difference from `listAccounts({ email })` is the whole reason this exists: that
   * filter is a substring, so `ann` matches `joanna@` too, the page is ordered
   * `createdAt DESC`, and nothing on it says which row the caller meant. This route answers
   * the one account or a 404, which `apiGetOrNull` turns into `null` — every other status
   * still throws, so a 403 from the policy stays as loud as it was.
   *
   * ## Why the encoding is not `encodeURIComponent` alone
   *
   * survey-api maps this as a **catch-all** segment, because a local part may contain `/`
   * (RFC 5321 atext), nothing in that repo validates a migrated address, and a single
   * segment would leave such an account unaddressable — a 404 for a row that exists. A
   * catch-all is matched against the *decoded* path, and `%2F` never reaches it: an escaped
   * slash 404s. So a literal slash has to survive encoding.
   *
   * Which is why the address is encoded **per segment** and why a `.` or `..` between two of
   * those slashes is refused rather than sent. The server is not the only thing that reads
   * this path: the URL parser behind `fetch` removes dot segments before the request leaves,
   * and it does so for their percent-encoded spellings as well — `%2e` and `%2E` are dot
   * segments to it, which is what makes escaping them no defence (measured, not assumed:
   * `e2e/specs/admin/account-detail.spec.ts` watched an escaped `..` still walk the request
   * up a level). Sent anyway, `..%2F..%2Fx@…` would leave here as a call to a **different**
   * admin route carrying the admin's bearer, and whatever came back would be rendered as an
   * account. There is no encoding that reaches the catch-all, so the honest answer is to say
   * the address cannot be looked up this way rather than to look something else up.
   *
   * An empty address is not sent either: it matches no route, and a request whose answer is
   * known is not worth a round trip.
   */
  getAccountByEmail(email: string, signal?: AbortSignal): Promise<Nullable<IAdminAccountDetail>> {
    if (email === '') return Promise.resolve(null);

    const segments = email.split('/');
    if (segments.some((segment) => segment === '.' || segment === '..')) {
      return Promise.reject(new Error(UNADDRESSABLE));
    }

    const addressed = segments.map(encodeURIComponent).join('/');
    return apiGetOrNull<IAdminAccountDetail>(`${ADMIN_BASE}/accounts/by-email/${addressed}`, {
      signal
    });
  },

  /**
   * Create a seat. Answers 201 with no body, so there is nothing to return and nothing to
   * merge into a list — the caller re-reads.
   *
   * The seat signs in once its owner redeems the invitation survey-api queues for it — sent to
   * the test inbox instead, where the deployment redirects.
   */
  async createAccount(body: ICreateAccount): Promise<void> {
    await apiRequest<void>('/accounts', { method: 'POST', body });
  },

  /**
   * Replace an account's Team Portal roles (SR-228), by account id — the detail carries it.
   *
   * An unchanged set is survey-api's no-op; the card sends none. A changed one revokes the
   * account's sessions, which the caller says.
   */
  async setRoles(accountId: string, roleIds: readonly string[]): Promise<void> {
    const body: ISetAccountRoles = { roleIds: [...roleIds] };
    await apiRequest<void>(`${ADMIN_BASE}/accounts/${encodeURIComponent(accountId)}/roles`, {
      method: 'PUT',
      body
    });
  },

  /**
   * Set an advisor's address (SR-416), by account id — the detail carries it. 204 and no
   * body; every session of the account is revoked and three mails go out (see
   * `ISetAccountEmail`). The card sends nothing for the address the account has already.
   */
  async setEmail(accountId: string, body: ISetAccountEmail): Promise<void> {
    await apiRequest<void>(`${ADMIN_BASE}/accounts/${encodeURIComponent(accountId)}/email`, {
      method: 'PUT',
      body
    });
  },

  /**
   * Disable an account or make it active again (SR-442), by account id — the detail carries it.
   * 204 and no body, whether or not the status changed; disabling revokes every session the
   * account holds (see `ISetAccountStatus`). The caller re-reads.
   */
  async setStatus(accountId: string, body: ISetAccountStatus): Promise<void> {
    await apiRequest<void>(`${ADMIN_BASE}/accounts/${encodeURIComponent(accountId)}/status`, {
      method: 'PUT',
      body
    });
  },

  /** The account's IdP invitations, newest first (SR-476), by account id. A 404 is the account
   *  gone, and throws. */
  listIdpInvitations(accountId: string, signal?: AbortSignal): Promise<IAdminIdpInvitation[]> {
    return apiRequest<IAdminIdpInvitation[]>(
      `${ADMIN_BASE}/accounts/${encodeURIComponent(accountId)}/idp-invitations`,
      { signal }
    );
  },

  /** Invite the account to sign in at the IdP (SR-476) — queued, or the live one at the same
   *  address re-sent (see `IInviteToIdp`). */
  inviteToIdp(accountId: string, body: IInviteToIdp): Promise<IIdpInvitationQueued> {
    return apiRequest<IIdpInvitationQueued>(
      `${ADMIN_BASE}/accounts/${encodeURIComponent(accountId)}/idp-invitations`,
      { method: 'POST', body }
    );
  },

  /**
   * Unlink the identity the account signs in with (SR-476). 204 and no body — also for an
   * account linked to none. Every session of the account is revoked and its live invitation
   * superseded; the IdP's user is untouched, so signing in with it again links whichever account
   * then holds a live invitation for its address.
   */
  async unlinkIdp(accountId: string): Promise<void> {
    await apiRequest<void>(`${ADMIN_BASE}/accounts/${encodeURIComponent(accountId)}/idp-link`, {
      method: 'DELETE'
    });
  }
};
